import { defineStore } from 'pinia';
import type { ApiResponse } from '@/composables/useApiService';

import type {
  RecoveryPasswordData,
  RecoveryPasswordFlow,
  UserDocument,
} from '@/types/UserData';
import { useUserJourney } from '~/composables/apostatudo/use-user-journey';

export const useAuthStore = defineStore('auth', () => {
  const { t } = useTexts();
  const { ApiService } = useApiService();

  const { fetchUserProfile } = useUserData();
  const { checkPendingContracts } = useUserValidations();
  const { openTwoFactor, closeAuth } = useAuth();
  const { openToaster, openChallenge, closeChallenge, openModal } =
    useLayoutStore();

  const { afterLoginRedirect } = storeToRefs(useLayoutStore());

  const { setLoggedOutByInactivity, resetTimeLoggedIn } =
    useUserActivityStore();

  const { checkCupomCode } = useCouponStore();

  const { saveToken, destroyToken } = useJwtStore();
  const { accessToken } = storeToRefs(useJwtStore());

  const { updateWallet } = useWallet();
  const { startSmartico, logoutSmartico } = useSmarticoInit();
  const { enterSiteEvent } = useUserJourney();

  const isLoggingOut = ref(false);

  const contactValidatedHandler = ref<
    | null
    | ((ctx: {
        optionId: Extract<Containment.PendencyName, 'email' | 'phone'>;
      }) => Promise<void> | void)
  >(null);

  const recoveryPasswordData = reactive<RecoveryPasswordData>(
    {} as RecoveryPasswordData,
  );

  const recoveryPasswordFlow = reactive<RecoveryPasswordFlow>(
    {} as RecoveryPasswordFlow,
  );

  const isAuthenticated = computed<boolean>(() => {
    return !!accessToken.value;
  });

  function setRecoveryPasswordData(payload: Partial<RecoveryPasswordData>) {
    Object.assign(recoveryPasswordData, payload);
  }

  const setRecoveryPasswordFlow = (payload: Partial<RecoveryPasswordFlow>) => {
    Object.assign(recoveryPasswordFlow, payload);
  };

  const resetRecoveryPasswordFlow = () => {
    Object.keys(recoveryPasswordFlow).forEach((key) => {
      delete recoveryPasswordFlow[key as keyof RecoveryPasswordFlow];
    });

    Object.keys(recoveryPasswordData).forEach((key) => {
      delete recoveryPasswordData[key as keyof RecoveryPasswordData];
    });
  };

  function onContactValidationComplete(
    handler: (ctx: {
      optionId: Extract<Containment.PendencyName, 'email' | 'phone'>;
    }) => Promise<void> | void,
  ) {
    contactValidatedHandler.value = handler;
  }

  async function handleContactValidationComplete(ctx: {
    optionId: Extract<Containment.PendencyName, 'email' | 'phone'>;
  }) {
    if (contactValidatedHandler.value) {
      await contactValidatedHandler.value(ctx);
    }

    contactValidatedHandler.value = null;
  }

  async function setAuth({ data, headers }: ApiResponse<LoginResponse>) {
    const { access_token } = data;

    if (!access_token) {
      return console.error('[Auth] setAuth aborted: missing access_token.');
    }

    saveToken(access_token);

    try {
      if (headers) {
        const xInfoIdHeader =
          // @ts-expect-error
          headers['X-INFOS-ID'] ||
          headers.get('X-INFOS-ID') ||
          headers.get('X-Infos-Id') ||
          headers.get('x-infos-id');

        const xInfoIdHeaderRef =
          // @ts-expect-error
          headers['X-INFOS-REF'] ||
          headers.get('X-INFOS-REF') ||
          headers.get('X-Infos-Ref') ||
          headers.get('x-infos-ref');

        if (xInfoIdHeader) {
          localStorage.setItem('c_infoId', xInfoIdHeader);
        }
        if (xInfoIdHeaderRef) {
          sessionStorage.setItem('c_infoIdRef', xInfoIdHeaderRef);
        }
      }
    } catch (error) {
      console.error('[Auth] Failed to persist login headers:', error);
    }

    await sleep(1);
    await fetchUserProfile({ force: true });

    setLoggedOutByInactivity(false);

    const { fetchAndStore } = useLastGames();
    const { fetchAndStore: fetchAndStoreFavorites } = useFavoriteGames();
    const { userId } = useUserData();

    if (userId.value) {
      fetchAndStoreFavorites(String(userId.value)).catch(() => {});
      fetchAndStore(userId.value).catch(() => {});
    }

    const { isValidationModuleEnabled, isTriggerModuleOnCurrentDevice } =
      useModulesValidation();

    const isGlobalModuleEnabled = isValidationModuleEnabled('global');

    if (isGlobalModuleEnabled) {
      const { $containment } = useNuxtApp();

      await $containment.fetchCurrentPending('global');

      if ($containment.hasPendencyOn('global')) {
        await $containment.handle('global', undefined, {
          action: 'redirect',
        });
      }
    }

    const isRegisterModuleEnabled =
      isValidationModuleEnabled('register') &&
      isTriggerModuleOnCurrentDevice('register');

    if (isRegisterModuleEnabled) {
      const { $containment } = useNuxtApp();

      await $containment.fetchCurrentPending('register');

      if ($containment.hasPendencyOn('register')) {
        await $containment.handle('register', undefined, {
          action: 'modal',
        });
      }
    }

    return await Promise.all([checkCupomCode(), updateWallet()]);
  }

  function sendSuccessLoginMessage<T = any>({ data, headers }: ApiResponse<T>) {
    if (window.self === window.top) {
      openToaster({
        message: t('auth.login.success'),
        type: 'success',
      });

      return;
    }

    window.parent.postMessage({
      message: 'loginSuccess',
      responseData: data,
      responseHeaders: {
        'X-INFOS-ID':
          headers.get('X-INFOS-ID') ||
          headers.get('X-Infos-Id') ||
          headers.get('x-infos-id'),
        'X-INFOS-REF':
          headers.get('X-INFOS-REF') ||
          headers.get('X-Infos-Ref') ||
          headers.get('x-infos-ref'),
      },
    });
  }

  function openErrorMessage(msg: string) {
    if (Array.isArray(msg)) {
      msg = msg.join(', ');
    }

    if (typeof msg === 'object') {
      msg = JSON.stringify(msg);
    }

    const message = msg || t('general.error');

    openToaster({
      message,
      type: 'error',
    });
  }

  function redirectAfterLogin() {
    if (!afterLoginRedirect.value) {
      return;
    }

    const redirect = afterLoginRedirect.value;

    afterLoginRedirect.value = '';

    if (redirect.includes('http')) {
      try {
        const target = new URL(redirect);
        const sameOrigin = target.origin === window.location.origin;

        if (sameOrigin) {
          window.location.assign(redirect);
        } else {
          window.location.assign('/');
        }
      } catch {
        window.location.assign('/');
      }

      return;
    }

    const isSafePath =
      typeof redirect === 'string' &&
      redirect.startsWith('/') &&
      !redirect.startsWith('//') &&
      !/^\/(en|es|es-mx|hi|de|fr|it|pt-br)(\/|$)/i.test(redirect) &&
      !/^\/[a-z][a-z0-9_-]*=/i.test(redirect);

    if (!isSafePath) {
      navigateTo('/');

      return;
    }

    navigateTo(localeLink(redirect) as string);
  }

  async function login(credentials: ILoginSubmitData) {
    return await ApiService.post<LoginResponse>(`/auth/login`, credentials)
      .then(async (response: any) => {
        if (localStorage.getItem('temp_login_email')) {
          localStorage.removeItem('temp_login_email');
        }

        await setAuth(response);

        checkPendingContracts();

        sendSuccessLoginMessage(response);

        redirectAfterLogin();

        closeAuth();

        resetRecoveryPasswordFlow();

        const { $asyncLoad } = useNuxtApp();

        $asyncLoad.injectScriptsIntoHead();

        const { isKycForcedRequest } = useUserData();

        if (isKycForcedRequest.value) {
          const { $containment } = useNuxtApp();
          const { getValidationModule } = useModulesValidation();

          $containment.updateConfig({
            global: [
              {
                name: 'kyc',
                frequent: true,
              },
              ...getValidationModule('global'),
            ],
          });

          const { goToLocaleRoute } = useRoutes();

          await goToLocaleRoute({ name: 'pending' });
        }

        if (response.status.toString().startsWith('20')) {
          console.info('[Smartico] Login successful. Starting Smartico SDK...');
          try {
            startSmartico();
          } catch (error) {
            console.error('[Smartico] Failed to start SDK after login:', error);
          }

          try {
            await enterSiteEvent();
          } catch (error) {
            console.error(
              '[UserJourney] Failed to send enterSiteEvent after login:',
              error,
            );
          }
        } else {
          openErrorMessage(t('auth.login.error.credentials'));
        }

        return response;
      })
      .catch((err: any) => {
        const errorData = err.data;

        try {
          if (errorData && errorData.error === 'attempt_limit') {
            if (errorData.kyc_recovery?.recovery_token) {
              setRecoveryPasswordFlow({
                token: errorData.kyc_recovery?.recovery_token,
              });
            }

            openModal('attempt-limit');

            return closeChallenge();
          }

          if (errorData && errorData.type === 'cancelled_account') {
            // TODO: Refatorar o cancelled_account para abrir dentro da modal
            // isReactivateAccountDialogVisible.value = true;
            // return;
          }

          if (errorData && errorData.type === 'two_factor_code') {
            openTwoFactor({
              loginData: credentials,
              type: errorData.two_factor_type,
            });

            return;
          }
        } catch (error) {
          console.error(
            '[Auth] Error while handling login response (#1):',
            error,
          );
          openErrorMessage(`${t('auth.login.error.general')}: #1`);

          return err;
        }

        try {
          if (err.status === 429) {
            openErrorMessage(t('auth.login.error.challenge'));

            openChallenge();

            return;
          }
        } catch (error) {
          console.error(
            '[Auth] Error while handling login response (#2):',
            error,
          );
          openErrorMessage(`${t('auth.login.error.general')}: #2`);

          return err;
        }

        try {
          if (errorData && errorData.type !== 'force_change_password') {
            const defaultMsg =
              err.status >= 500
                ? t('auth.login.error.default_msg')
                : t('auth.login.error.credentials');

            const errorMessage = errorData.message
              ? errorData.message
              : errorData.error
                ? errorData.error
                : defaultMsg;

            openErrorMessage(errorMessage);
          }
        } catch (error) {
          console.error(
            '[Auth] Error while handling login response (#3):',
            error,
          );
          openErrorMessage(`${t('auth.login.error.general')}: #3`);

          return err;
        }

        throw err;
      });
  }

  interface LogoutOptions {
    displayMessage: boolean;
    redirectToHome: boolean;
    origin?: 'user' | 'system';
  }

  async function logout(options?: LogoutOptions) {
    if (isLoggingOut.value) {
      console.warn('[Auth] logout skipped: logout is already in progress.');

      return;
    }

    isLoggingOut.value = true;

    try {
      await executeLogout(options);
    } finally {
      isLoggingOut.value = false;
    }
  }

  async function executeLogout(options?: LogoutOptions) {
    const hadActiveSession = !!accessToken.value;
    const isUserInitiated = options?.origin === 'user';

    if (accessToken.value) {
      try {
        await ApiService.post('/auth/logout', {});
      } catch (error) {
        console.error('[Auth] Logout API request failed:', error);
      }
    }

    destroyToken();

    const userData = useUserDataStore();
    const walletStore = useWalletStore();
    const userLimits = useUserLimitsStore();
    const userHistory = useUserHistoryStore();
    const userActivity = useUserActivityStore();
    const smarticoUser = useSmarticoUserInfos();
    const smarticoMissions = useSmarticoMissions();
    const kycStore = useKycStore();

    userData.$reset();
    walletStore.$reset();
    userLimits.$reset();
    userHistory.$reset();
    userActivity.$reset();
    smarticoUser.$reset();
    smarticoMissions.$reset();
    kycStore.$reset();

    resetTimeLoggedIn();

    try {
      localStorage.removeItem('c_infoId');
      sessionStorage.removeItem('c_infoIdRef');
      useOpenModalAfterTime().clear();

      const { clearStorage } = useLastGames();
      const { clearStorage: clearFavoritesStorage } = useFavoriteGames();

      clearStorage();
      clearFavoritesStorage();
    } catch (error) {
      console.error('[Auth] Failed to clear local session data:', error);
    }

    let isSmarticoLogoutEventSent = false;

    if (hadActiveSession) {
      console.info('[Smartico] Starting Smartico logout process...');

      isSmarticoLogoutEventSent = logoutSmartico();

      const reInitDelayMs = isSmarticoLogoutEventSent ? 1600 : 0;

      setTimeout(() => {
        console.info(
          '[Smartico] Re-initializing SDK after logout (visitor mode)...',
        );
        try {
          startSmartico();
        } catch (error) {
          console.error(
            '[Smartico] Failed to re-initialize SDK after logout:',
            error,
          );
        }
      }, reInitDelayMs);
    }

    try {
      const { $containment } = useNuxtApp();

      if ($containment?.closeVerificationModal) {
        $containment.closeVerificationModal();
      }
    } catch (error) {
      console.error(
        '[Auth] Failed to close containment verification modal:',
        error,
      );
    }

    if (options?.redirectToHome) {
      navigateTo(localeLink('/') as string);
    }

    if (options?.displayMessage) {
      try {
        const { logoutZendeskUser } = useLiveSupport();

        logoutZendeskUser();
      } catch (error) {
        console.error('[Auth] Failed to logout Zendesk user:', error);
      }

      if (hadActiveSession && !isSmarticoLogoutEventSent) {
        console.warn(
          '[Smartico] Logout sync failed: "user_logged_out" event may not have been sent.',
        );
      }

      setTimeout(() => {
        openToaster({
          message: t('auth.login.logout_msg'),
          type: 'info',
        });
      }, 1000);
    }

    if (isUserInitiated) {
      try {
        await enterSiteEvent();
      } catch (error) {
        console.error(
          '[UserJourney] Failed to send enterSiteEvent after user logout:',
          error,
        );
      }
    }
  }

  async function forgotPassword(
    data: IForgotPassword | IPasswordRecoveryAttemptLimit,
  ) {
    return await ApiService.post('/auth/passwords/reset/options', data)
      .then((res: any) => {
        return res;
      })
      .catch((err: any) => {
        openErrorMessage(err.data?.message);

        return err;
      });
  }

  async function verifyDocument(data: IVerifyDocument) {
    return await ApiService.post<UserDocument>('/documents/validate', data)
      .then((res: any) => {
        return res.data;
      })
      .catch((err: any) => {
        return err.data;
      });
  }

  async function changePassword(id: number, data: IChangePassword) {
    return await ApiService.post(`/users/change-password/${id}`, data)
      .then((res: any) => {
        return res;
      })
      .catch((err: any) => {
        openErrorMessage(err.data?.error);

        return err;
      });
  }

  return {
    accessToken,
    isAuthenticated,
    recoveryPasswordData,
    recoveryPasswordFlow,
    contactValidatedHandler,
    onContactValidationComplete,
    handleContactValidationComplete,
    login,
    setAuth,
    logout,
    forgotPassword,
    changePassword,
    verifyDocument,
    setRecoveryPasswordData,
    setRecoveryPasswordFlow,
    resetRecoveryPasswordFlow,
  };
});
